/**
 * Web Vitals tracking for Core Web Vitals and custom performance metrics.
 *
 * Tracks:
 * - CLS (Cumulative Layout Shift)
 * - INP (Interaction to Next Paint)
 * - LCP (Largest Contentful Paint)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 */

import { onCLS, onLCP, onFCP, onTTFB, onINP, type Metric } from 'web-vitals'

export interface PerformanceMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  timestamp: number
}

// Thresholds based on Google's Core Web Vitals recommendations
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // ms
  INP: { good: 200, poor: 500 }, // ms
  CLS: { good: 0.1, poor: 0.25 }, // score
  FCP: { good: 1800, poor: 3000 }, // ms
  TTFB: { good: 800, poor: 1800 }, // ms
}

/**
 * Get rating for a metric value based on thresholds.
 */
function getRating(
  metricName: string,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[metricName as keyof typeof THRESHOLDS]
  if (!threshold) return 'good'

  if (value <= threshold.good) return 'good'
  if (value <= threshold.poor) return 'needs-improvement'
  return 'poor'
}

/**
 * Format metric for logging/reporting.
 */
function formatMetric(metric: Metric): PerformanceMetric {
  return {
    name: metric.name,
    value: metric.value,
    rating: getRating(metric.name, metric.value),
    timestamp: Date.now(),
  }
}

/**
 * Send metrics to analytics endpoint (customize based on your needs).
 */
function sendToAnalytics(metric: PerformanceMetric) {
  // Only send in production
  if (import.meta.env.DEV) {
    console.log('[Performance]', metric.name, {
      value: metric.value.toFixed(2),
      rating: metric.rating,
    })
    return
  }

  // In production, you could send to your analytics service
  // Example: Google Analytics, DataDog, New Relic, etc.
  // navigator.sendBeacon('/api/analytics', JSON.stringify(metric))

  // For now, just log in production too (can be removed)
  console.log('[Performance]', metric.name, {
    value: metric.value.toFixed(2),
    rating: metric.rating,
  })
}

/**
 * Initialize Web Vitals tracking.
 * Call this once in your app's entry point.
 */
export function initWebVitals() {
  // Core Web Vitals
  onCLS((metric: Metric) => sendToAnalytics(formatMetric(metric)))
  onINP((metric: Metric) => sendToAnalytics(formatMetric(metric)))
  onLCP((metric: Metric) => sendToAnalytics(formatMetric(metric)))

  // Additional metrics
  onFCP((metric: Metric) => sendToAnalytics(formatMetric(metric)))
  onTTFB((metric: Metric) => sendToAnalytics(formatMetric(metric)))
}

/**
 * Track custom performance marks.
 */
export function trackPerformanceMark(name: string) {
  if (typeof performance === 'undefined') return

  performance.mark(name)
}

/**
 * Measure duration between two marks.
 */
export function measurePerformance(name: string, startMark: string, endMark: string) {
  if (typeof performance === 'undefined') return

  try {
    performance.measure(name, startMark, endMark)
    const measure = performance.getEntriesByName(name, 'measure')[0]

    if (measure) {
      const metric: PerformanceMetric = {
        name,
        value: measure.duration,
        rating: measure.duration < 100 ? 'good' : measure.duration < 300 ? 'needs-improvement' : 'poor',
        timestamp: Date.now(),
      }
      sendToAnalytics(metric)
    }
  } catch (error) {
    console.warn('Failed to measure performance:', error)
  }
}

/**
 * Track component render time (use in development).
 */
export function trackComponentRender(componentName: string, renderTime: number) {
  if (import.meta.env.PROD) return // Only track in dev

  const metric: PerformanceMetric = {
    name: `render:${componentName}`,
    value: renderTime,
    rating: renderTime < 16 ? 'good' : renderTime < 50 ? 'needs-improvement' : 'poor',
    timestamp: Date.now(),
  }

  console.log('[Component Render]', componentName, {
    time: renderTime.toFixed(2) + 'ms',
    rating: metric.rating,
  })
}

/**
 * Get navigation timing metrics.
 */
export function getNavigationMetrics() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return null
  }

  const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]

  if (!navigationEntry) return null

  return {
    dns: navigationEntry.domainLookupEnd - navigationEntry.domainLookupStart,
    tcp: navigationEntry.connectEnd - navigationEntry.connectStart,
    ttfb: navigationEntry.responseStart - navigationEntry.requestStart,
    download: navigationEntry.responseEnd - navigationEntry.responseStart,
    domInteractive: navigationEntry.domInteractive - navigationEntry.fetchStart,
    domComplete: navigationEntry.domComplete - navigationEntry.fetchStart,
    loadComplete: navigationEntry.loadEventEnd - navigationEntry.fetchStart,
  }
}

/**
 * Log all navigation metrics (useful for debugging).
 */
export function logNavigationMetrics() {
  const metrics = getNavigationMetrics()
  if (!metrics) return

  console.group('[Navigation Timing]')
  console.log('DNS lookup:', metrics.dns.toFixed(2) + 'ms')
  console.log('TCP connection:', metrics.tcp.toFixed(2) + 'ms')
  console.log('TTFB:', metrics.ttfb.toFixed(2) + 'ms')
  console.log('Download:', metrics.download.toFixed(2) + 'ms')
  console.log('DOM Interactive:', metrics.domInteractive.toFixed(2) + 'ms')
  console.log('DOM Complete:', metrics.domComplete.toFixed(2) + 'ms')
  console.log('Load Complete:', metrics.loadComplete.toFixed(2) + 'ms')
  console.groupEnd()
}
